import type { BookSection } from '@/components/books/book';
import { getReferenceStorage } from '@/components/tiptap-editor/tiptap-extension/reference-extension/reference-store';
import { buildResourceNumberLookup } from '@/components/tiptap-editor/tiptap-extension/reference-extension/reference-utils';
import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { stripCommentAnnotationsFromDoc } from '@/lib/comment-utils';
import { getEditorVersionFilter } from '@/lib/zy-tiptap-utils';
import { getMaDocument, getTiptapDocumentJson, type ReferenceToContentResources } from '@/lib/zybooks-api';
import { convertProseMirrorToZybooksJSON } from '@/lib/zybooks-parser';
import type { ZybookTestQuestion } from '@/lib/zybooks-parser/from-test-question';
import {
    convertProsemirrorTestQuestionsToZybook,
    type ConvertProsemirrorToZybookTestQuestionsOptions,
} from '@/lib/zybooks-parser/to-zybooks-test-question';
import type { Editor, JSONContent } from '@tiptap/react';

export interface ConvertEditorOptions {
    /** Single version to use for zyVersion filtering during export/preview conversion. */
    previewVersion?: string;

    /** Runs before getting the editor's JSON */
    prepareEditor?: (editor: Editor) => void;

    /** Whether this is a Modern Authoring preview (generates launch data) */
    isMAPreview?: boolean;

    /** ZyBook code for preview launch data generation */
    zybookCode?: string | null;
}

export interface ConvertEditorResult {
    jsonContent: JSONContent;
    contentResources: any[];
    enumMaps: Partial<ReferenceToContentResources>;
}

export type PreviewTestQuestionsResult = {
    testQuestions: ZybookTestQuestion[] | undefined;
    error?: string;
};

/**
 * Returns a copy of the editor document's {@link ZyDocumentAttributes}.
 */
export function getPreviewDocumentAttributes(editor: Editor | null): Partial<ZyDocumentAttributes> {
    return (editor?.$doc.attributes as Partial<ZyDocumentAttributes> | undefined) ?? {};
}

export function parseBookSectionNumber(number: string): { chapterNumber: number; sectionNumber: number } | null {
    const [chapterPart, sectionPart] = number.split('.', 2);
    const chapterNumber = Number(chapterPart);
    const sectionNumber = Number(sectionPart);
    if (!Number.isFinite(chapterNumber) || !Number.isFinite(sectionNumber)) {
        return null;
    }
    return { chapterNumber, sectionNumber };
}

export function mergeMaSectionDocAttributes(
    setupDocAttributes: Partial<ZyDocumentAttributes>,
    existingDocAttributes: Partial<ZyDocumentAttributes>,
    section: BookSection,
): Partial<ZyDocumentAttributes> {
    const merged = { ...setupDocAttributes };

    const existingTitleDefault = existingDocAttributes.title?.default?.trim();
    const setupTitleDefault = setupDocAttributes.title?.default?.trim();

    if (!existingTitleDefault && !setupTitleDefault) {
        merged.title = { ...existingDocAttributes.title, ...setupDocAttributes.title, default: section.title };
    }

    return merged;
}

/**
 * Clones `jsonContent` and injects resolved `displayText`/`href` into every
 * reference node so that `renderHTML()` can emit the formatted label and link
 * rather than the static "REFERENCE" placeholder.
 */
function addReferenceDisplayDataForPreview(jsonContent: JSONContent, editor: Editor): JSONContent {
    const { referencesByKey } = getReferenceStorage(editor);
    const currentSectionId: string | undefined = editor.state.doc.attrs?.sectionId;
    const zybookCode: string | undefined = editor.state.doc.attrs?.zybookCode;
    // Computed lazily — only needed for in-section destination references.
    let resourceNumberLookup: Record<string, string | number> | null = null;

    function addDisplayData(node: JSONContent): JSONContent {
        const updatedChildren = node.content?.map(addDisplayData);

        if (node.type !== 'reference') {
            return updatedChildren ? { ...node, content: updatedChildren } : node;
        }

        const referenceNodeId: string | undefined = node.attrs?.id;
        const stored = currentSectionId && referenceNodeId ? (referencesByKey[currentSectionId]?.[referenceNodeId] ?? null) : null;

        if (!stored) {
            return {
                ...node,
                attrs: { ...node.attrs, displayText: '⚠ Broken section reference' },
                ...(updatedChildren ? { content: updatedChildren } : {}),
            };
        }

        const destination = stored.destination;
        const chapterNum = destination.chapter_number;
        const sectionNum = destination.section_number;

        if (chapterNum == null || sectionNum == null) {
            return {
                ...node,
                attrs: { ...node.attrs, displayText: '⚠ Broken reference' },
                ...(updatedChildren ? { content: updatedChildren } : {}),
            };
        }

        const isInSection = !!destination.section_guid && destination.section_guid === currentSectionId;
        const destinationLookupId = destination.sub_node_id ?? destination.node_id ?? null;

        let resourceNumber: string | number | null;
        if (isInSection && destinationLookupId) {
            resourceNumberLookup ??= buildResourceNumberLookup(editor.state.doc);
            resourceNumber = resourceNumberLookup[destinationLookupId] ?? null;
        } else {
            resourceNumber = destination.resource_number ?? null;
        }

        const isBroken = !!destination.node_id && resourceNumber == null;
        let displayText: string;
        if (isBroken) {
            displayText = `\u26a0 ${chapterNum}.${sectionNum}`;
        } else {
            const resourceSuffix = resourceNumber != null ? `${typeof resourceNumber === 'number' ? '.' : ''}${resourceNumber}` : '';
            displayText = `${chapterNum}.${sectionNum}${resourceSuffix}`;
        }

        let href: string | undefined;
        if (!isBroken && destination.ma_document_id != null && zybookCode) {
            const nodeQuery = destination.node_id
                ? `?nodeId=${encodeURIComponent(destination.node_id)}&openPreview=true`
                : '?openPreview=true';
            href = `${window.location.origin}/books/${encodeURIComponent(zybookCode)}/${destination.ma_document_id}${nodeQuery}`;
        }

        return {
            ...node,
            attrs: { ...node.attrs, displayText, href: href ?? null },
            ...(updatedChildren ? { content: updatedChildren } : {}),
        };
    }

    return addDisplayData(jsonContent);
}

/**
 * Serializes a TipTap editor document to ProseMirror JSON and zyBooks section content resources JSON.
 *
 * @param editor - Source editor
 * @param options - Optional `prepareEditor` (runs before serialization) and `previewVersion`. See {@link ConvertEditorOptions}.
 * @returns The JSON snapshot that was converted plus `contentResources` and `enumMaps` from {@link convertProseMirrorToZybooksJSON}.
 */
export async function convertEditorToZybooksSectionResources(editor: Editor, options?: ConvertEditorOptions): Promise<ConvertEditorResult> {
    options?.prepareEditor?.(editor);

    const previewVersion = options?.previewVersion;
    const jsonContent = editor.getJSON();
    const selectedZyVersion = previewVersion !== undefined ? previewVersion : (getEditorVersionFilter(editor) ?? null);

    const jsonContentWithReferenceData = addReferenceDisplayDataForPreview(jsonContent, editor);
    const jsonContentForExport = stripCommentAnnotationsFromDoc(jsonContentWithReferenceData);

    const { contentResources, enumMaps } = await convertProseMirrorToZybooksJSON(jsonContentForExport, {
        isMAPreview: options?.isMAPreview,
        zybookCode: options?.zybookCode,
        selectedZyVersion,
    });
    return { jsonContent, contentResources, enumMaps };
}

export async function resolvePreviewTestQuestions(options: {
    mainEditor: Editor;
    linkedTestQuestionDocumentIds?: number[];
    convertOptions?: ConvertProsemirrorToZybookTestQuestionsOptions;
    previewVersion?: string;
}): Promise<PreviewTestQuestionsResult> {
    const docType = (options.mainEditor.$doc?.attributes as Partial<ZyDocumentAttributes> | undefined)?.documentType;

    if (docType === 'test_question') {
        return {
            testQuestions: await convertProsemirrorTestQuestionsToZybook(options.mainEditor.getJSON(), {
                ...options.convertOptions,
                selectedZyVersion: options.previewVersion,
            }),
        };
    }

    const linkedIds = (options.linkedTestQuestionDocumentIds ?? []).filter(
        (id, index, ids) => Number.isInteger(id) && id > 0 && ids.indexOf(id) === index,
    );

    if (linkedIds.length === 0) {
        return { testQuestions: undefined };
    }

    try {
        const testQuestions: ZybookTestQuestion[] = [];
        for (const linkedId of linkedIds) {
            const maDocument = await getMaDocument(linkedId);
            const tiptapJson = await getTiptapDocumentJson(maDocument.document_name);
            testQuestions.push(
                ...(await convertProsemirrorTestQuestionsToZybook(tiptapJson, {
                    ...options.convertOptions,
                    selectedZyVersion: options.previewVersion,
                })),
            );
        }
        return { testQuestions };
    } catch (error) {
        return {
            testQuestions: undefined,
            error: error instanceof Error ? error.message : 'Failed to load linked test questions',
        };
    }
}
